import { useAction, useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import {
  AttentionIcon,
  Button,
  CircularLoader,
  CloseIcon,
  Refresh2Icon,
} from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { mediaMaxMobile, PlusIcon } from '@root/shared/ui-kit'

import { supportDialogOnlineAtom, supportDialogScreenAtom } from '../atoms'
import { supportTicketsModel } from '../atoms/reatom-support-ticket'

import { SupportWelcomeHeader } from './support-welcome-header'

import type { ClassNameProps } from '@root/shared/lib/react'

type SupportWelcomeProps = {
  onClose?: () => void
  busy?: boolean
  error?: boolean
} & ClassNameProps

export const SupportWelcome: React.FC<SupportWelcomeProps> = ({
  className,
  onClose,
  busy,
  error,
}) => {
  const { t } = useTranslation()
  const [online] = useAtom(supportDialogOnlineAtom)
  const [, setScreen] = useAtom(supportDialogScreenAtom)
  const handleRetry = useAction(supportTicketsModel.refresh)

  const handleCreateTicket = () => setScreen({ screen: 'create' })

  return (
    <Root className={className}>
      {onClose ? (
        <ButtonClose
          size="s"
          variant="ghost"
          iconRight={<CloseIcon />}
          onClick={onClose}
        />
      ) : undefined}

      <SupportWelcomeHeader online={online} />
      {!busy && !error ? (
        <>
          <WelcomeMessage>{t('support.welcome-message')}</WelcomeMessage>
          <ButtonCreate
            size="h32"
            variant="soft"
            radius="rounded"
            iconLeft={<PlusIcon />}
            onClick={handleCreateTicket}
          >
            {t('support.create-new-ticket')}
          </ButtonCreate>
        </>
      ) : undefined}
      {busy ? <Loader size={22} color="gray" /> : undefined}
      {error ? (
        <Error>
          <AttentionIcon size={18} color="var(--background-10)" />
          <Description>{t('common.something-went-wrong')}</Description>
          <ButtonRetry
            variant="soft"
            size="h28"
            radius="rounded"
            iconLeft={<Refresh2Icon />}
            onClick={handleRetry}
          >
            {t('common.retry')}
          </ButtonRetry>
        </Error>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  padding: 40px 38px;
  display: grid;
  place-content: center;
  justify-items: center;
  justify-content: center;

  ${mediaMaxMobile(css`
    padding: 30px 38px;
  `)}
`

const WelcomeMessage = styled.p`
  color: var(--text-9);
  text-align: center;

  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  margin-top: 23px;
`

const ButtonCreate = styled(Button)`
  font-weight: 500;
  margin-top: 15px;
`

const ButtonClose = styled(Button)`
  color: var(--text-9);

  position: absolute;
  right: 0;
  top: 6px;
  z-index: 1;

  &:hover {
    color: var(--text-12);
    background: transparent;
  }
`

const Loader = styled(CircularLoader)`
  margin-block: 50px 30px;
`

const Error = styled.div`
  margin-top: 20px;

  display: grid;
  gap: 2px;
  justify-items: center;
`

const ButtonRetry = styled(Button)`
  font-weight: 500;
  margin-top: 10px;
`

const Description = styled.p`
  color: var(--text-10);
  text-align: center;

  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`
